"use client";

import { useEffect, useState } from "react";
import { Bell, MessageCircle } from "lucide-react";
import { io } from "socket.io-client";
import { api, SOCKET_BASE } from "@/lib/api";
import { getSession } from "@/lib/auth";
import type { Conversation } from "@/lib/types";
import { JobChat } from "./JobChat";

export function ChatNotificationBell() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  async function refresh() {
    try {
      setConversations(await api.getConversations() as Conversation[]);
    } catch {
      setConversations([]);
    }
  }

  useEffect(() => {
    const session = getSession();
    if (!session?.token || session.role === "admin") return;
    void refresh();
    const interval = window.setInterval(refresh, 15000);
    const socket = io(SOCKET_BASE, {
      auth: { token: session.token },
    });
    socket.on("notification:new-message", () => {
      void refresh();
      setOpen(true);
    });
    return () => {
      window.clearInterval(interval);
      socket.disconnect();
    };
  }, []);

  const unread = conversations.reduce((sum, item) => sum + (item.unreadCount || 0), 0);

  return (
    <>
      <div className="relative">
        <button onClick={() => setOpen((value) => !value)} className="relative rounded-xl border border-slate-200 bg-white p-3 text-slate-500 hover:text-emerald-700">
          <Bell size={19} />
          {unread > 0 && <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-center text-[10px] font-black text-white">{unread}</span>}
        </button>
        {open && (
          <div className="absolute right-0 z-50 mt-3 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-100 px-5 py-4"><p className="font-black">Messages</p><p className="text-xs font-semibold text-slate-400">{unread} unread</p></div>
            <div className="max-h-80 overflow-y-auto">
              {conversations.length === 0 ? <p className="p-8 text-center text-sm font-semibold text-slate-400">No conversations yet</p> : conversations.map((item) => (
                <button key={item.id} onClick={() => { setJobId(item.jobId); setOpen(false); }} className="flex w-full items-center gap-3 border-b border-slate-100 px-5 py-4 text-left hover:bg-slate-50">
                  <div className="rounded-xl bg-emerald-50 p-2 text-emerald-700"><MessageCircle size={17} /></div>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-black">{item.participant.name}</p><p className="truncate text-xs font-semibold text-slate-400">{item.job.category}</p></div>
                  {(item.unreadCount || 0) > 0 && <span className="rounded-full bg-emerald-600 px-2 py-1 text-[10px] font-black text-white">{item.unreadCount}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {jobId && <JobChat jobId={jobId} onClose={() => { setJobId(null); void refresh(); }} />}
    </>
  );
}
